import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';

function envGet(key: string, fallback?: string) {
  return (import.meta as any).env?.[`VITE_${key}`] || (window as any).__env?.[key] || fallback;
}

const API_BASE = envGet('API_BASE_URL', '');

@Injectable({ providedIn: 'root' })
export class ApiService {
  private anonIdKey = 'fb-anon-id';

  constructor(private auth: AuthService) {
    if (!localStorage.getItem(this.anonIdKey)) {
      localStorage.setItem(this.anonIdKey, Math.random().toString(36).slice(2, 10));
    }
  }

  private anonId(): string {
    return localStorage.getItem(this.anonIdKey)!;
  }

  private async headers(authRequired = false): Promise<Record<string, string>> {
    const h: Record<string, string> = { 'Content-Type': 'application/json' };
    if (authRequired) {
      const token = await this.auth.getIdToken();
      // API Gateway HTTP API (JWT authorizer) expects a Bearer token
      if (token) h['Authorization'] = `Bearer ${token}`;
    } else {
      h['X-Anonymous-Id'] = this.anonId();
    }
    return h;
  }

  private ensureApiBase() {
    if (!API_BASE) {
      const msg = 'API_BASE_URL ist nicht konfiguriert. Bitte setze VITE_API_BASE_URL oder src/assets/env.js.';
      console.error(msg);
      throw new Error(msg);
    }
  }

  private async fetchJson(path: string, opts: RequestInit = {}, authRequired = false) {
    this.ensureApiBase();
    const res = await fetch(`${API_BASE}${path}`, {
      ...opts,
      headers: {
        ...(await this.headers(authRequired)),
        ...(opts.headers as any)
      }
    });
    const ct = res.headers.get('content-type') || '';
    if (!res.ok) {
      const text = ct.includes('application/json') ? JSON.stringify(await res.json()) : await res.text();
      throw new Error(`HTTP ${res.status}: ${text}`);
    }
    if (ct.includes('application/json')) {
      return res.json();
    } else {
      const text = await res.text();
      throw new Error(`Erwartete JSON-Antwort, erhalten: ${ct || 'unbekannt'} – Inhalt beginnt mit: ${text.slice(0, 120)}`);
    }
  }

  async listFestivals() {
    return this.fetchJson(`/festivals`, { method: 'GET' }, false);
  }

  async listBands(festivalId: string) {
    return this.fetchJson(`/festivals/${encodeURIComponent(festivalId)}/bands`, { method: 'GET' }, false);
  }

  async getRatings(festivalId: string) {
    const user = await this.auth.currentUser();
    const path = user ? `/ratings?festivalId=${encodeURIComponent(festivalId)}` : `/anon/ratings?festivalId=${encodeURIComponent(festivalId)}`;
    return this.fetchJson(path, { method: 'GET' }, !!user);
  }

  async updateRating(festivalId: string, bandId: string, patch: { rating?: number; note?: string }) {
    const user = await this.auth.currentUser();
    const path = user ? `/ratings` : `/anon/ratings`;
    return this.fetchJson(path, {
      method: 'PUT',
      body: JSON.stringify({ festivalId, bandId, ...patch })
    }, !!user);
  }

  async shareCreate(festivalId: string, aliasOrToken: string | undefined, includeNotes: boolean) {
    return this.fetchJson(`/share`, {
      method: 'POST',
      body: JSON.stringify({ festivalId, aliasOrToken, includeNotes })
    }, true);
  }

  async shareGet(festivalId: string, aliasOrToken: string) {
    return this.fetchJson(`/share/${encodeURIComponent(aliasOrToken)}?festivalId=${encodeURIComponent(festivalId)}`, { method: 'GET' }, false);
  }

  async shareList() {
    return this.fetchJson(`/share`, { method: 'GET' }, true);
  }

  async shareDelete(festivalId: string, aliasOrToken: string) {
    return this.fetchJson(`/share`, {
      method: 'DELETE',
      body: JSON.stringify({ festivalId, aliasOrToken })
    }, true);
  }

  async userSettingsGet() {
    return this.fetchJson(`/settings`, { method: 'GET' }, true);
  }

  async userSettingsPut(labels: string[]) {
    return this.fetchJson(`/settings`, {
      method: 'PUT',
      body: JSON.stringify({ labels })
    }, true);
  }

  async linkAnonymous() {
    const anonId = this.anonId();
    return this.fetchJson(`/auth/link-anonymous`, {
      method: 'POST',
      body: JSON.stringify({ anonId })
    }, true);
  }

  // Admin
  async adminFestivals() {
    return this.fetchJson(`/admin/festivals`, { method: 'GET' }, true);
  }

  async adminFestivalCreate(id: string, name: string, imageUrl?: string) {
    return this.fetchJson(`/admin/festivals`, {
      method: 'POST',
      body: JSON.stringify({ id, name, imageUrl })
    }, true);
  }

  async adminFestivalUpdate(id: string, patch: { name?: string; imageUrl?: string }) {
    return this.fetchJson(`/admin/festivals/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(patch)
    }, true);
  }

  async adminFestivalDelete(id: string) {
    return this.fetchJson(`/admin/festivals/${encodeURIComponent(id)}`, { method: 'DELETE' }, true);
  }

  async adminBandsImport(festivalId: string, data: any[]) {
    return this.fetchJson(`/admin/festivals/${encodeURIComponent(festivalId)}/bands/import`, {
      method: 'POST',
      body: JSON.stringify({ data })
    }, true);
  }

  async adminBandCreate(festivalId: string, band: { bandId: string; name: string; spotifyArtistId?: string; thumbnail?: string; detailLink?: string }) {
    return this.fetchJson(`/admin/festivals/${encodeURIComponent(festivalId)}/bands`, {
      method: 'POST',
      body: JSON.stringify(band)
    }, true);
  }

  async adminBandUpdate(festivalId: string, bandId: string, patch: { name?: string; spotifyArtistId?: string; thumbnail?: string; detailLink?: string }) {
    return this.fetchJson(`/admin/festivals/${encodeURIComponent(festivalId)}/bands/${encodeURIComponent(bandId)}`, {
      method: 'PUT',
      body: JSON.stringify(patch)
    }, true);
  }

  async adminBandDelete(festivalId: string, bandId: string) {
    return this.fetchJson(`/admin/festivals/${encodeURIComponent(festivalId)}/bands/${encodeURIComponent(bandId)}`, { method: 'DELETE' }, true);
  }

  async adminDefaultLabelsPut(labels: string[]) {
    return this.fetchJson(`/admin/settings/default-labels`, {
      method: 'PUT',
      body: JSON.stringify({ labels })
    }, true);
  }

  async adminFestivalImageUploadUrl(festivalId: string, contentType: string) {
    return this.fetchJson(`/admin/festivals/${encodeURIComponent(festivalId)}/image/upload-url`, {
      method: 'POST',
      body: JSON.stringify({ contentType })
    }, true);
  }
}
